/**
 * Product card behaviour:
 *   - quick add buttons [data-quick-add data-variant-id]
 *   - quick view buttons [data-quick-view data-url] open one shared <dialog>, filled from
 *     the product's quick-view section (Lumi.quickView.open / close)
 *   - add buttons on products with options [data-quick-choose data-url] open the same panel as a
 *     sidebar on the right (bottom sheet on phones), so the shopper picks before anything is added
 *   - card image gets a view-transition-name on click so it can morph into the product page
 */
(function () {
  if (window.__lumiProductCardLoaded) return;
  window.__lumiProductCardLoaded = true;

  const Lumi = (window.Lumi = window.Lumi || {});
  const str = (key, fallback) => (Lumi.strings && Lumi.strings[key]) || fallback;
  const CLOSE_ICON =
    '<svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m6 6 12 12M18 6 6 18" stroke-linecap="round"/></svg>';

  /* ---------- Quick add ---------- */
  async function quickAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      await Lumi.cart.add([{ id: Number(button.dataset.variantId), quantity: 1 }]);
      Lumi.emit('lumi:cart:open');
      Lumi.announce(str('addedToCart', 'Added to your cart'));
    } catch (err) {
      Lumi.announce((err && err.message) || str('cartError', 'That did not work. Please try again.'));
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------- Quick view dialog ---------- */
  const state = { dialog: null, body: null, trigger: null, controller: null, closing: null };

  function ensureDialog() {
    if (state.dialog) return state.dialog;
    const dialog = document.createElement('dialog');
    dialog.className = 'qv-dialog';
    dialog.id = 'QuickViewDialog';
    dialog.setAttribute('scroll-lock', '');
    dialog.setAttribute('aria-label', str('quickView', 'Quick view'));
    dialog.innerHTML =
      `<button type="button" class="qv-dialog__close" data-qv-close aria-label="${str('quickViewClose', 'Close')}">${CLOSE_ICON}</button>` +
      '<div class="qv-dialog__body" data-qv-body></div>';
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-qv-close]')) close();
    });
    dialog.addEventListener('close', onClosed);
    document.body.appendChild(dialog);
    state.dialog = dialog;
    state.body = dialog.querySelector('[data-qv-body]');
    return dialog;
  }

  function status(message, loading) {
    const el = document.createElement('div');
    el.className = 'qv-dialog__status';
    el.setAttribute('role', 'status');
    if (loading) el.setAttribute('data-loading', '');
    const text = document.createElement('p');
    text.className = loading ? 'visually-hidden' : '';
    text.textContent = message;
    el.appendChild(text);
    return el;
  }

  // mode 'side' (choose options, then add) docks the panel to the right edge on desktop; phones keep the bottom sheet.
  async function open(url, trigger, { mode } = {}) {
    const dialog = ensureDialog();
    if (state.controller) state.controller.abort();
    const controller = (state.controller = new AbortController());
    state.trigger = trigger || document.activeElement;

    const side = mode === 'side';
    dialog.dataset.mode = side ? 'side' : 'view';
    dialog.removeAttribute('aria-labelledby');
    dialog.setAttribute('aria-label', side ? str('chooseOptions', 'Choose options') : str('quickView', 'Quick view'));
    dialog.removeAttribute('data-closing');
    state.body.setAttribute('aria-busy', 'true');
    state.body.replaceChildren(status(str('quickViewLoading', 'Loading product'), true));

    if (!dialog.open) {
      dialog.showModal();
      Lumi.lockScroll(true);
      Lumi.emit('lumi:dialog:opened', { id: dialog.id });
    }

    try {
      const html = await Lumi.fetchSection(url, 'quick-view', { signal: controller.signal });
      const panel = Lumi.parseHTML(html).querySelector('quick-view-panel');
      if (!panel) throw new Error('Quick view section missing');
      state.body.replaceChildren(panel);
      state.body.removeAttribute('aria-busy');
      if (Lumi.wishlist) Lumi.wishlist.sync(state.body);
      const title = panel.querySelector('[data-qv-title]');
      if (title) {
        dialog.setAttribute('aria-labelledby', title.id);
        dialog.removeAttribute('aria-label');
        title.focus({ preventScroll: true });
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      const box = status(str('quickViewError', 'This product could not be loaded here. Open the product page instead.'), false);
      const link = document.createElement('a');
      link.href = url;
      link.className = 'btn btn--ghost';
      link.textContent = str('quickViewDetails', 'View full details');
      box.appendChild(link);
      state.body.replaceChildren(box);
      state.body.removeAttribute('aria-busy');
    }
  }

  // Plays the exit animation, then closes. Resolves once the dialog is closed.
  function close() {
    const dialog = state.dialog;
    if (!dialog || !dialog.open) return Promise.resolve();
    if (state.closing) return state.closing;
    if (state.controller) state.controller.abort();
    state.closing = new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.removeEventListener('animationend', finish);
        dialog.close();
        dialog.removeAttribute('data-closing');
        state.closing = null;
        resolve();
      };
      dialog.addEventListener('animationend', finish);
      dialog.setAttribute('data-closing', '');
      setTimeout(finish, 300);
    });
    return state.closing;
  }

  function onClosed() {
    Lumi.lockScroll(false);
    state.body.replaceChildren();
    const trigger = state.trigger;
    state.trigger = null;
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    Lumi.emit('lumi:dialog:closed', { id: state.dialog.id });
  }

  Lumi.quickView = { open, close };

  /* ---------- Delegated clicks ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target.closest && event.target;
    if (!target) return;

    const addBtn = target.closest('[data-quick-add]');
    if (addBtn) {
      event.preventDefault();
      quickAdd(addBtn);
      return;
    }

    // Products with options: Add to cart opens the picker as a sidebar so the shopper chooses first.
    const chooseBtn = target.closest('[data-quick-choose]');
    if (chooseBtn) {
      event.preventDefault();
      open(chooseBtn.dataset.url, chooseBtn, { mode: 'side' });
      return;
    }

    const viewBtn = target.closest('[data-quick-view]');
    if (viewBtn) {
      event.preventDefault();
      open(viewBtn.dataset.url || `/products/${viewBtn.dataset.handle}`, viewBtn);
      return;
    }

    // Name the clicked card's tile so the browser can morph it into the product page media.
    const link = target.closest('.product-card__link');
    if (link) {
      document.querySelectorAll('.product-card__tile[style*="view-transition-name"]').forEach((t) => (t.style.viewTransitionName = ''));
      const tile = link.closest('.product-card').querySelector('.product-card__tile');
      if (tile) tile.style.viewTransitionName = 'lumi-product-media';
    }
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.product-card__tile').forEach((t) => (t.style.viewTransitionName = ''));
  });
})();
